import { cn } from "@/lib/utils";
import { DailyViewLimit } from "@/utils/daily-view-limit";
import { useEffect, useRef, useState } from "react";

interface Props {
  active: boolean;
  tooltip: string;
  /** 추천 종류별 노출 기록 키 (예: `ROLL_FORWARD`). 워크북이 아니라 계정 단위로 센다. */
  storageKey: string;
  children: React.ReactNode;
  className?: string;
}

const VIEWS_PER_DAY = 2;

const viewLimitOf = (storageKey: string) =>
  new DailyViewLimit(
    `tagging-guidance-tooltip-shown:${storageKey}`,
    VIEWS_PER_DAY,
  );

// Figma Shadow/Medium — toggle-ui-kit Tooltip 과 같은 값.
const SHADOW_MEDIUM =
  "shadow-[0_2px_4px_4px_rgba(0,0,0,0.06),0_4px_6px_-1px_rgba(0,0,0,0.1)]";

// 자식 버튼 배경과 무관하게 "테두리만" 그리는 그라데이션 링.
// (outlined 버튼은 배경이 투명이라 뒤에 깔면 배경으로 비친다 — mask 로 링만 남긴다.)
const GRADIENT_RING_STYLE: React.CSSProperties = {
  padding: "2px",
  backgroundImage:
    "linear-gradient(90deg, #4B7BF5, #9B5BE8, #E85BC7, #9B5BE8, #4B7BF5)",
  WebkitMask:
    "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
  WebkitMaskComposite: "xor",
  maskComposite: "exclude",
};

/**
 * 추천된 다음 액션 버튼을 테두리 강조 + 툴팁으로 안내한다.
 *
 * 테두리는 `active` 인 동안 계속 보인다. 툴팁은 그보다 조심스럽게 — 바깥을 클릭하면
 * 사라지고, 같은 추천의 툴팁은 하루 2회까지만 뜬다(워크북이 아니라 계정 기준).
 * 추천이 바뀌면 다시 판단하도록 호출부가 추천 값을 `key` 로 넘겨 재마운트시킨다.
 */
export function TaggingGuidanceHighlight({
  active,
  tooltip,
  storageKey,
  children,
  className,
}: Props) {
  const [claimed, setClaimed] = useState(false);
  const [tooltipDismissed, setTooltipDismissed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const showTooltip = active && claimed && !tooltipDismissed;

  // 노출 여부를 묻는 동시에 오늘치 한 번을 차감한다 — 새로고침·재진입해도 하루 2회.
  useEffect(() => {
    if (!active) return;
    setClaimed(viewLimitOf(storageKey).claim());
  }, [active, storageKey]);

  // halo(focus 링)가 처음부터 켜져 있도록 추천 버튼에 포커스를 준다. 화면을 스크롤해
  // 버튼을 끌어오지는 않는다 — 안내일 뿐 사용자의 자리를 옮길 일은 아니다.
  //
  // active 는 추천 쿼리 응답 뒤에 켜지므로 사용자 조작과 무관한 시점에 이 effect 가 돈다.
  // 이미 어딘가에 포커스가 있다면(셀 편집·검색창 타이핑) 뺏지 않는다.
  useEffect(() => {
    if (!active) return;
    const focused = document.activeElement;
    if (focused && focused !== document.body) return;
    ref.current?.querySelector("button")?.focus({ preventScroll: true });
  }, [active]);

  // 워크북에 처음 들어오면 사이드바가 열린 채라 첫 클릭이 거의 항상 오버레이에 떨어진다.
  // 그 클릭은 "사이드바 닫기"일 뿐 안내를 떠난 것이 아니므로, mousedown 의 기본 동작인
  // 포커스 이동만 막아 halo 를 지킨다 — 오버레이의 click(닫기)은 그대로 일어난다.
  useEffect(() => {
    if (!active) return;
    const keepFocusOnOverlay = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-sidebar-overlay]")) event.preventDefault();
    };
    document.addEventListener("mousedown", keepFocusOnOverlay);
    return () => document.removeEventListener("mousedown", keepFocusOnOverlay);
  }, [active]);

  useEffect(() => {
    if (!showTooltip) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // 사이드바 오버레이 클릭은 "사이드바 닫기"일 뿐 힌트를 본 것이 아니다. 워크북에
      // 처음 들어오면 사이드바가 열린 채라 첫 클릭이 거의 항상 여기에 떨어진다.
      if (target.closest("[data-sidebar-overlay]")) return;
      if (ref.current && !ref.current.contains(target)) {
        setTooltipDismissed(true);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [showTooltip]);

  if (!active) return <>{children}</>;

  return (
    <div
      ref={ref}
      className={cn(
        "relative inline-flex rounded-small",
        // halo 는 focus 링이다 — 다른 곳을 클릭해 포커스가 빠지면 halo 만 걷히고
        // 그라데이션 테두리는 남는다. 상태를 따로 들고 있지 않아도 된다.
        // 정지한 링은 눈에 안 들어온다 — 포커스가 있는 동안 halo 가 계속 번쩍인다.
        // 애니메이션을 끈 환경에서는 정적 halo 로 남는다.
        "focus-within:shadow-[0_0_0_4px_rgba(21,93,252,0.45)]",
        "focus-within:animate-halo-pulse motion-reduce:animate-none",
        className,
      )}
    >
      {children}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10 rounded-small animate-in fade-in-0 zoom-in-95 fill-mode-forwards motion-reduce:animate-none"
        style={GRADIENT_RING_STYLE}
      />
      {/* 크기·색은 toggle-ui-kit Tooltip(Figma Design System) 과 맞춘다 —
          이 툴팁만 크면 안내가 아니라 소음이 된다. */}
      {showTooltip && (
        <div
          className={cn(
            "absolute left-1/2 top-[calc(100%+6px)] z-50 w-max -translate-x-1/2",
            "whitespace-pre-line rounded-[4px] bg-primary-base px-2 py-1.5",
            "text-center text-static-white label-2-medium",
            SHADOW_MEDIUM,
          )}
        >
          <div className="absolute -top-[3px] left-1/2 size-[8px] -translate-x-1/2 rotate-45 rounded-[1px] bg-primary-base" />
          {tooltip}
        </div>
      )}
    </div>
  );
}
